import React, { Component, Fragment } from 'react';
import { ApiHelper } from '../helpers/ApiHelper';
import { Helper } from '../helpers/Helper';
import { StyleSheet, css } from 'aphrodite';

import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Select from '@material-ui/core/Select';
import Checkbox from '@material-ui/core/Checkbox';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import { ImageUploader } from './ImageUploader';
import { DocumentUploader } from './DocumentUploader';
import { RichtextEditor, parseRichTextToolbarFlags } from './RichtextComponent/RichtextEditor';
import { AppTheme } from '../../styles/AppTheme';
import TextField from '@material-ui/core/TextField';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CloseIcon from '@material-ui/icons/Close';
import { ScheduleDurationPicker } from './ScheduleDurationPicker';

import Accordion from '@material-ui/core/ExpansionPanel';
import AccordionSummary from '@material-ui/core/ExpansionPanelSummary';
import AccordionDetails from '@material-ui/core/ExpansionPanelDetails';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import moment from 'moment-timezone'
import MomentUtils from '@date-io/moment'
import { AppConfig } from '../../AppConfig';
import { MuiPickersUtilsProvider, TimePicker } from 'material-ui-pickers'
import { DatePicker } from 'material-ui-pickers'
import { DateTimePicker } from 'material-ui-pickers'
import { CustomDynamicFormStyles } from '../../styles/CustomStyles';
import { deleteFormField } from '../data/data';
import { AppButton } from './AppButton';
import { IconButton, InputAdornment, Tooltip } from '@material-ui/core';
import ImageIcon from '@material-ui/icons/Image';
import { ColourPicker } from './ColourPicker';
import { Controller } from '../Controller';
import { SubEntityCard } from './SubEntity/SubEntityCard';
import { ReactDateRangeWrapper } from './DateRangeComponent/ReactDateRangeWrapper';
import { NumberRangePicker } from './NumberRangePicker';
import { FormPreviewPane } from './FormPreviewPane';
import { applyFlatListFieldsToFormData, getListConfig } from '../helpers/ListFlatStorageHelper';
import { UiHelper } from '../helpers/UiHelper';

function newListRowId() {
  return `lr_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
}

function newListRow(rowFields) {
  const row = { id: newListRowId() }
  const initializedKeys = new Set()
  for (const rf of rowFields || []) {
    if (!rf || !rf.key || initializedKeys.has(rf.key)) {
      continue
    }
    initializedKeys.add(rf.key)
    if (Object.prototype.hasOwnProperty.call(rf, 'defaultValue')) {
      row[rf.key] = rf.defaultValue
    } else if (rf.type === 'check_box') {
      row[rf.key] = []
    } else {
      // Keep radio/select cells blank until chosen (do not default to options[0])
      row[rf.key] = ''
    }
  }
  return row
}

/**
 * List UI updates rows by `row.id`. FieldDef defaults / BE-hydrated mappings often omit ids
 * (or reuse them), which makes one cell edit patch every row. Clone and mint unique ids.
 */
function ensureListRowsHaveIds(rows) {
  if (!Array.isArray(rows)) {
    return []
  }
  const seen = new Set()
  return rows.map((row) => {
    const next = { ...(row && typeof row === 'object' ? row : {}) }
    if (!next.id || seen.has(next.id)) {
      next.id = newListRowId()
    }
    seen.add(next.id)
    return next
  })
}

function listCellHasValue(rf, value, listField) {
  const cfg = getListConfig(listField)
  const emptyVals = cfg.flatEmptyValues || {}
  if (rf && rf.key && Object.prototype.hasOwnProperty.call(emptyVals, rf.key)) {
    if (value === undefined || value === null || value === '' || String(value) === String(emptyVals[rf.key])) {
      return false
    }
  }
  if (rf && rf.type === 'check_box') {
    return Array.isArray(value) && value.length > 0
  }
  if (Array.isArray(value)) {
    return value.length > 0 && String(value[0] || '').trim().length > 0
  }
  return value != null && String(value).trim().length > 0
}

function getMinMaxError(field, value) {
  if (field.type !== 'edit_text' || field.imageUpload || field.documentUpload || field.subType === 'image' || field.subType === 'document') {
    return null
  }
  if (value === undefined || value === null || value === '') {
    return null
  }
  const compared = (field.v_int || field.v_float) ? Number(value) : String(value).length
  if (field.v_min && field.v_min.value != null && compared < field.v_min.value) {
    return field.v_min.err || `${field.label || 'Value'} is invalid`
  }
  if (field.v_max && field.v_max.value != null && compared > field.v_max.value) {
    return field.v_max.err || `${field.label || 'Value'} is invalid`
  }
  return null
}

function isListRowEmpty(row, rowFields, listField) {
  return !(rowFields || []).some((rf) => rf && rf.key && listCellHasValue(rf, row && row[rf.key], listField))
}

function hasListRowErrors(listRowErrors) {
  if (!listRowErrors || typeof listRowErrors !== 'object') {
    return false
  }
  return Object.keys(listRowErrors).some((rowId) => {
    const rowErrs = listRowErrors[rowId]
    return rowErrs && Object.keys(rowErrs).length > 0
  })
}

export class DynamicForm extends Component {
  constructor(props) {
    super(props);
    this.submitOnlyChanges = false
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleSelectChange = this.handleSelectChange.bind(this);
    this.doAction = this.doAction.bind(this);
    this.updateListField = this.updateListField.bind(this);
    this.addListRow = this.addListRow.bind(this);
    this.removeListRow = this.removeListRow.bind(this);
    this.moveListRow = this.moveListRow.bind(this);
    this.isColourPickerPresent = false
    this.state = {
      success: true,
      message: '',
      inProcess: false,
      formFieldUpdateProgress: null,
      colorPickerLoading:true,
      filterOperatorOpenModalKey: null,
      brandColors:[],
      selectedColor:{
        color:'',
        title:''
      },
      isAdvancedOpen: false,
      isAccountsOpen: false,
    };
    this.locations = {}
    this.setFormState();
    this.currOrgId = Controller.get().roleHelper().getOrgId()
    this.groupId = 'settings' + this.currOrgId
  }

  setFormState() {
    this.fields = this.props.formDefinition.steps[0].fields;
    for (let i = 0; i < this.fields.length; i++) {
      const current = this.fields[i];

      if (current.type === 'radio') {
        current.multiSelect = false
        if (current.value && Array.isArray(current.value) && current.value.length > 0) {
          // incase the cloud stores a radio as singleton array
          current.value = current.value[0]
        }
      } else if (current.type === 'check_box') {
        current.multiSelect = true
      }
      if (current.type === 'list') {
        if (!Array.isArray(current.value)) {
          current.value = []
        } else {
          current.value = ensureListRowsHaveIds(current.value)
        }
      } else if ((this.props.formDefinition.formType === 'add')  && current.defaultValue) {
        current.value = current.defaultValue
      }
      const valueMissing =
        current.value === undefined ||
        current.value === null ||
        (typeof current.value === 'string' && current.value === '')
      if (valueMissing) {
        if (current.type === 'list') {
          current.value = []
        } else if (current.multiSelect) {
          current.value = [];
        } else {
          current.value = '';
        }
      }

      if (current.type === 'list') {
        const cfg = getListConfig(current);
        const minItems = cfg.minItems || 0;
        while (current.value.length < minItems) { current.value.push(newListRow(cfg.rowFields)); }
      }

      if (!this.state[current.key]) {
        this.state[current.key] = current.value;
        if (current.valueDisplayScale && this.state[current.key]) {
          this.state[current.key] = (this.state[current.key] / current.valueDisplayScale).toFixed(2)
        }
      } else if (
        current.multiSelect &&
        Array.isArray(current.value) &&
        current.value.length > 0 &&
        !Array.isArray(this.state[current.key])
      ) {
        // Duplicate keys (e.g. rule filter `value`): a radio field ran first and
        // collapsed the array to a string. Restore the array so check_box hydrates.
        this.state[current.key] = current.value
      }
      this.fields[i].valid = true;
      if (!this.fields[i].v_required) {
        this.fields[i].v_required = {
          value: Boolean(this.fields[i].required),
          err: this.fields[i].label ? `${this.fields[i].label} is required` : 'Required',
        };
      }
      // if (current.imageUpload || current.documentUpload) { //TODO: why whas this introduced? commenting now since we are honouring 'disabled' attributes now
      //   current.disabled = true
      // }
    }
    this.syncSyntheticOptionsForSelectLikeFields()
    this.previousState = this.state;
  }

  /** `_syntheticOptionKeys` must be a Set; JSON clone turns Set into `{}` and breaks `.add`. */
  normalizeSyntheticOptionKeysSet(field) {
    const raw = field && field._syntheticOptionKeys
    if (raw && typeof raw.add === 'function' && typeof raw.has === 'function') {
      return raw
    }
    const next = new Set()
    if (Array.isArray(raw)) {
      raw.forEach((k) => { if (k != null && k !== '') next.add(String(k)) })
    }
    if (field) {
      field._syntheticOptionKeys = next
    }
    return next
  }

  /**
   * Ensures current state values exist in options for select-like fields only when the field's
   * condition matches; removes entries we added when the condition stops matching.
   */
  syncSyntheticOptionsForSelectLikeFields() {
    for (const current of this.fields) {
      const resolved = this.resolveFieldForOperator(current)
      if (!['radio', 'check_box', 'select'].includes(resolved.type)) {
        continue
      }
      if (!resolved.options) {
        resolved.options = []
      }
      const syntheticKeys = this.normalizeSyntheticOptionKeysSet(current)
      if (!this.isConditionMatch(current)) {
        if (syntheticKeys.size > 0) {
          resolved.options = resolved.options.filter(o => !syntheticKeys.has(o.key))
          syntheticKeys.clear()
        }
        continue
      }

      const raw = this.state[current.key] !== undefined ? this.state[current.key] : current.value
      if (!raw) {
        continue
      }
      const values = Array.isArray(raw) ? raw : [raw]
      for (const val of values) {
        if (val === undefined || val === null || val === '') {
          continue
        }
        if (!resolved.options.some(obj => obj.key === val)) {
          resolved.options.push({ key: val, label: val })
          syntheticKeys.add(val)
        }
      }
    }
  }

  ensureListRowSelectOptions(field, rowValue) {
    if (!field || !['radio', 'check_box'].includes(field.type)) {
      return
    }
    if (!field.options) {
      field.options = []
    }
    const values = field.type === 'check_box'
      ? (Array.isArray(rowValue) ? rowValue : (rowValue ? [rowValue] : []))
      : (rowValue != null && rowValue !== '' ? [String(rowValue)] : [])
    for (let i = 0; i < values.length; i++) {
      const val = values[i]
      if (val && !field.options.some((o) => o.key === val)) {
        field.options.push({ key: val, label: val })
      }
    }
  }

  handleInputChange(event) {
    const target = event.target;
    const value = target.value;
    const id = target.id;
    this.fields.find(o => o.key === id).valid = true;
    this.setFieldValueAndResetDependents(id, value);
  }

  handleDeleteFieldChange = (event) => {
    const target = event.target;
    const value = target.value;
    const id = target.id;
    this.setState({
      [id]: value
    });
  }

  handleRichtextInputChange = (value, id) => {
    this.setFieldValueAndResetDependents(id, value);
  }

  handleSelectChange(event) {
    const target = event.target;
    const value = target.value;
    const name = target.name;
    this.fields.find(o => o.key === name).valid = true;
    this.setFieldValueAndResetDependents(name, value);
  }

  handleUploadSuccess(fieldKey, signedUploadInfo) {
    const field = this.fields.find(o => o.key === fieldKey);
    if (field) {
      field.valid = true
      const uploadedLink = (signedUploadInfo && signedUploadInfo.link) || ''
      field.link = uploadedLink
      field._uploadedImgdimensions = {
        height: signedUploadInfo && signedUploadInfo.height,
        width: signedUploadInfo && signedUploadInfo.width
      }
      this._lastPreviewFormData = null
      const assetKey = (signedUploadInfo && signedUploadInfo.fields && signedUploadInfo.fields.key != null) ? signedUploadInfo.fields.key : ''
      const nextState = { [fieldKey]: assetKey }
      if (field.linkName) {
        nextState[field.linkName] = uploadedLink
      }
      this.setState(nextState)
    }
  }

  handleColorChange = (color, id) => {
    this.setState({ [id]: color })
    this.setState({selectedColor:{color,title:color}})
  }

  checkValidityIfDeleteMode = () => {
    if (this.props.formDefinition.skipDeleteConfirmInput) {
      return false
    }
    if (this.props.formDefinition.formType === 'del' || this.props.formDefinition.formType === 'delete') {
      if (this.state.delete !== 'delete') {
        return true
      }
    }
    return false
  }

  doAction() {
    this.setState({
      inProcess: true, // Nothing should change while sumitting
      message: ''
    });

    const apiData = this.processValuesBeforeAction()
    if (!apiData) return
    this.submit(apiData)

    if(this.isColourPickerPresent) {
      this.updateColors()
    }
  }

  updateColors() {
    const colors = [...this.state.brandColors]
    const currIdx =  colors.findIndex(col=>col.code.toLowerCase() === this.state.selectedColor.color.toLowerCase())

    if(currIdx === -1 ) {
      colors.push({code:this.state.selectedColor.color, title:this.state.selectedColor.color})
    }

    if(colors.length > 25) {
      colors.splice(6,1) //NOTE: The first 6 are default colors coming from backend, so not deleting the same.
    }

    ApiHelper.call({
      method: 'PUT',
      paths:['items',this.groupId + '-all'],
      queryParams: {
        groupId:this.groupId
      },
      jsonBody:{colourhistory:colors}
    },function(err,data) {
      console.log(err,'COLOR_UPDATE_ERROR');
      console.log(data,'COLOR_UPDATE_DATA');
    })

  }

  isConditionMatch(current, stateOverride) {
    if (current.condition) {
      const state = stateOverride || this.state
      const statements = Array.isArray(current.condition.and) ? current.condition.and : [current.condition]
      for (let i = 0; i < statements.length; ++i) {
        const condition = statements[i]
        const conditionState = state[condition.key]
        const conditionValues = (condition.values || [condition.value])
        const isConditionStateInValues = Array.isArray(conditionState) ?
          conditionState.some(cs => Helper.inArray(conditionValues, cs)) :
          Helper.inArray(conditionValues, conditionState)
        const conditionOp = (condition.op || 'is')
        if (conditionState === undefined ||
          (
            (conditionOp === 'is' && !isConditionStateInValues) ||
            (conditionOp === 'not' && isConditionStateInValues) ||
            (conditionOp === 'notnull' && !(Array.isArray(conditionState) ? conditionState.length > 0 : conditionState))
          )
        ) {
          return false
        }
      }
    }
    return true
  }

  isRowConditionMatch(row, condition) {
    if (!condition || !row) {
      return true
    }
    const statements = Array.isArray(condition.and) ? condition.and : [condition]
    for (let i = 0; i < statements.length; ++i) {
      const c = statements[i]
      const conditionState = row[c.key]
      const conditionValues = (c.values || [c.value])
      const isConditionStateInValues = Array.isArray(conditionState) ? conditionState.some(cs => Helper.inArray(conditionValues, cs)) : Helper.inArray(conditionValues, conditionState)
      const conditionOp = (c.op || 'is')
      if (conditionState === undefined || ((conditionOp === 'is' && !isConditionStateInValues) || (conditionOp === 'not' && isConditionStateInValues) || (conditionOp === 'notnull' && !(Array.isArray(conditionState) ? conditionState.length > 0 : conditionState)))) {
        return false
      }
    }
    return true
  }
  isConditionalOn(field, key) {
    if (field.condition) {
      const statements = Array.isArray(field.condition.and) ? field.condition.and : [field.condition]
      for (let i = 0; i < statements.length; ++i) {
        const condition = statements[i]
        if (condition.key === key) { return true }
      }
    }
    return false
  }

  getFieldOperator(field) {
    return field.selectedOperator || field.defaultOperator
  }

  isOperatorConditionMatch(variant, field) {
    if (!variant.operatorCondition) {
      return false
    }
    const operator = this.getFieldOperator(field)
    const conditionValues = variant.operatorCondition.values || [variant.operatorCondition.value]
    return Helper.inArray(conditionValues, operator)
  }

  resolveFieldForOperator(field) {
    const formType = this.props.formDefinition && this.props.formDefinition.formType
    // Entity add/edit dialogs must use the full field definition (options catalog), not filter operator variants.
    if (formType === 'add' || formType === 'edit') {
      return field
    }
    if (!field.operatorVariants || !field.operatorVariants.length) {
      return field
    }
    const match = field.operatorVariants.find((variant) => this.isOperatorConditionMatch(variant, field))
    if (!match) {
      return field
    }
    const { operatorVariants, ...base } = field
    const { operatorCondition, ...variantProps } = match
    return { ...base, ...variantProps, key: field.key }
  }

  getFieldDefaultValue(field) {
    if (field.type === 'list') {
      const cfg = getListConfig(field)
      const minItems = cfg.minItems || 0
      const value = Array.isArray(field.defaultValue)
        ? ensureListRowsHaveIds(field.defaultValue)
        : []
      while (value.length < minItems) {
        value.push(newListRow(cfg.rowFields))
      }
      return value
    }
    if (Object.prototype.hasOwnProperty.call(field, 'defaultValue')) {
      return field.defaultValue
    }
    return field.multiSelect ? [] : ''
  }

  collectDependentResetValues(changedKeys = [], pendingState = {}, options = {}) {
    const onlyOnConditionFail = !!options.onlyOnConditionFail
    const queue = [...changedKeys]
    const visited = new Set(changedKeys)
    const valuesToReset = {}
    const resolveState = () => ({ ...this.state, ...pendingState, ...valuesToReset })
    while (queue.length > 0) {
      const key = queue.shift()
      for (const field of this.fields) {
        if (visited.has(field.key)) continue
        if (!this.isConditionalOn(field, key)) continue
        // Opt-in: skip reset when the dependent's condition still matches after the change.
        if (onlyOnConditionFail && this.isConditionMatch(field, resolveState())) continue
        const resetValue = this.getFieldDefaultValue(field)
        valuesToReset[field.key] = resetValue
        field.value = resetValue
        field.valid = true
        visited.add(field.key)
        queue.push(field.key)
      }
    }
    return valuesToReset
  }

  setFieldValueAndResetDependents(key, value) {
    const field = this.fields.find(o => o.key === key)
    if (field && field.preserveDependentValuesOnChange) {
      this.setState({ [key]: value })
      return
    }
    const pendingState = { [key]: value }
    const dependentResets = this.collectDependentResetValues([key], pendingState, {
      onlyOnConditionFail: !!(field && field.resetDependentsOnlyOnConditionFail),
    })
    const nextState = {
      ...dependentResets,
      [key]: value,
    }
    this.setState(nextState)
  }

  resolveCtaButtonText() {
    const by = this.props.formDefinition.ctaButtonTextBy
    if (by && by.key) {
      const fieldValue = this.state[by.key]
      if (by.map && Object.prototype.hasOwnProperty.call(by.map, fieldValue)) {
        return by.map[fieldValue]
      }
      if (by.default != null) return by.default
    }
    return this.props.formDefinition.ctaButtonText
  }

  isPreviewActive() {
    const preview = this.props.formDefinition.preview
    if (!preview || !preview.component) return false
    if (preview.condition && !this.isConditionMatch(preview)) return false
    return true
  }

  isValid(current) {
    let valid = true;
    current.valid = true;
    current._minMaxErr = null
    const resolved = this.resolveFieldForOperator(current)
    if (resolved.type === 'list') {
      return this.validateListField(current)
    }
    if (current.v_required && current.v_required.value) {
      if (!this.isConditionMatch(current)) {
        // If condition doesnt match we can ignore this field.
        current.valid = true;
        // continue;
      } else if (resolved.multiSelect) {
        if (!this.state[current.key].length) {
          valid = false;
          current.valid = false;
        }
      } else {
        if (this.state[current.key] === '') {
          valid = false;
          current.valid = false;
        }
      }
    }
    // On edit, don't block submit for pre-existing values the user hasn't touched (e.g. legacy data exceeding v_max)
    const isUnchangedOnEdit =
      this.props.formDefinition.formType === 'edit' &&
      this.previousState &&
      this.state[current.key] === this.previousState[current.key]
    const minMaxErr = valid && !isUnchangedOnEdit && this.isConditionMatch(current) ? getMinMaxError(resolved, this.state[current.key]) : null
    if (minMaxErr) {
      valid = false
      current.valid = false
      current._minMaxErr = minMaxErr
    }
    return valid
  }

  validateListField(current) {
    current.valid = true
    current._listRowErrors = {}
    if (!this.isConditionMatch(current)) {
      return true
    }
    const cfg = getListConfig(current)
    const rowFields = cfg.rowFields || []
    const rows = Array.isArray(this.state[current.key]) ? this.state[current.key] : []
    const nonEmptyRows = rows.filter((row) => !isListRowEmpty(row, rowFields, current))

    if (current.v_required && current.v_required.value && nonEmptyRows.length === 0) {
      current.valid = false
      return false
    }

    for (const row of rows) {
      if (isListRowEmpty(row, rowFields, current)) {
        continue
      }
      for (const rf of rowFields) {
        if (!rf) {
          continue
        }
        if (rf.condition && !this.isRowConditionMatch(row, rf.condition)) {
          continue
        }
        let err = null
        if (rf.v_required && rf.v_required.value && !listCellHasValue(rf, row[rf.key], current)) {
          err = rf.v_required.err || (rf.label ? `${rf.label} is required` : 'Required')
        } else {
          err = getMinMaxError(rf, row[rf.key])
        }
        if (err) {
          current.valid = false
          if (!current._listRowErrors[row.id]) {
            current._listRowErrors[row.id] = {}
          }
          current._listRowErrors[row.id][rf.key] = err
        }
      }
    }
    return current.valid
  }

  processValuesBeforeAction() {
    const apiData = Object.assign({}, this.props.apiData)
    let hasChange = false;
    let valid = true;
    delete apiData.REMOVE_ATTRS
    const REMOVE_ATTRS = []
    for (const current of this.fields) {
      if(this.props.formDefinition.formType !== 'info') {
        this.isValid(current)
        if (!current.valid) {
          valid = false
          if (current.accounts) {
            this.setState({ isAccountsOpen: true })
          } else if(current.advanced) {
            this.setState({isAdvancedOpen : true})
          }
        }
      }
      apiData[current.key] = this.state[current.key];
      if (current.addOptionsFormatter && apiData[current.key] != null && apiData[current.key] !== '') {
        apiData[current.key] = Helper.formatAddOptionFieldValue(apiData[current.key], current.addOptionsFormatter)
      }
      if (current.valueDisplayScale && apiData[current.key]) {
        apiData[current.key] =
          Math.round(apiData[current.key] * current.valueDisplayScale)
      }
      if (this.submitOnlyChanges) {
        if (this.state[current.key] !== this.previousState[current.key]) {
          hasChange = true;
        } else {
          delete apiData[current.key]
        }
      }
      const value = this.state[current.key];
      if (!current.v_required || !current.v_required.value) {
        if (value !== this.previousState[current.key]) {
          if (value === '' || value === null || value === undefined) {
            if (!REMOVE_ATTRS.includes(current.key)) {
              REMOVE_ATTRS.push(current.key)
            }
            delete apiData[current.key]
          }
        }
      }
    }
    if (REMOVE_ATTRS.length) {
      apiData.REMOVE_ATTRS = REMOVE_ATTRS
    }

    if (!valid) {
      this.setState({
        success: false,
        inProcess: false,
      });
      return null;
    }

    if (this.submitOnlyChanges && !hasChange && this.props.formDefinition.formType === 'edit') {
      this.setState({
        success: false,
        message: 'No changes',
        inProcess: false
      });
      return null;
    }

    if (this.checkValidityIfDeleteMode()) {
      this.setState({
        success: false,
        inProcess: false,
        message: Helper.getString('confirmDeleteMsg')
      });
      return null;
    }

    if (this.locations) {
      for (const key in this.locations) {
        apiData[key] = this.locations[key]
      }
    }
    applyFlatListFieldsToFormData(apiData, this.fields, this.state, (field) => this.isConditionMatch(field))
    return apiData;
  }

  submit(apiData, ignoreCustomSubmit = false) {
    if((this.props.formDefinition.formType === 'filter') && this.props.onFilterSubmit) {
      this.props.onFilterSubmit({
        formType: this.props.formDefinition.formType,
        formData: apiData,
        filterOptions: JSON.parse(JSON.stringify(this.fields)),
        caller: this
      }, () => {
        if (this.props.onClosed) {
          this.props.onClosed();
        }
      })
      return
    }
    if (this.props.customSubmit && !ignoreCustomSubmit) {
      this.props.customSubmit({
        formType: this.props.formDefinition.formType,
        formData: apiData,
        caller: this
      }, () => {
        if (this.props.onClosed) {
          this.props.onClosed();
        }
      })
      return
    }
    ApiHelper.call(
      { method: this.props.apiMethod, endPoint: this.props.apiEndPoint, jsonBody: apiData },
      function (err, data) {
        if (err) {
          const errorMessage = Helper.getErrorMsg(err);
          this.setState({
            fetchState: ApiHelper.State.ERROR,
            errMsg: errorMessage,
            success: false,
            message: errorMessage,
            inProcess: false
          })
          return;
        }
        this.previousState = this.state;
        if (Helper.inArray(['delete', 'del'], this.props.formDefinition.formType)) {
          this.setState({
            success: true,
            message: '',
            inProcess: false
          })
          // if (this.props.onClosed) {
          //   this.props.onClosed();
          // }
          // if (this.props.onCompleteAction) {
          //   this.props.onCompleteAction()
          // }
        } else {
          this.setState({
            success: true,
            message: Helper.getString('successful'),
            inProcess: false
          })
        }
        if (this.props.onCompleteAction) {
          this.props.onCompleteAction({
            apiInvokeData: {
              method: this.props.apiMethod,
              endPoint: this.props.apiEndPoint,
              reqBody: apiData,
              responseBody: data
            }
          })
        }
      }.bind(this));
  }

  setFormControlParams(current, fromControlParams) {
    if (current.v_required && current.v_required.value) {
      fromControlParams.required = true;
    }
    if (!current.valid) {
      fromControlParams.error = true;
    }
  }

  handleFilterOperatorChange = (event, current) => {
    const newValue = event.target.value;
    const oldOperator = this.getFieldOperator(current);
    const oldResolved = this.resolveFieldForOperator({ ...current, selectedOperator: oldOperator });
    const newResolved = this.resolveFieldForOperator({ ...current, selectedOperator: newValue });
    const existingFields = JSON.parse(JSON.stringify(this.fields));
    const updatedFields = existingFields.map(field => {
      delete field._syntheticOptionKeys
      if (field.key === current.key) {
        field.selectedOperator = newValue
      }
      return field;
    });

    this.fields = updatedFields;
    const needsReset = oldResolved.type !== newResolved.type || !!oldResolved.multiSelect !== !!newResolved.multiSelect;
    if (needsReset) {
      this.setState({ [current.key]: this.getFieldDefaultValue(newResolved) });
    } else {
      this.forceUpdate()
    }
  }


  _preFillTip(current) {
    if(current.type !== 'rich_text') return
    if(current.subType === 'h1') {
      current.tip = Helper.getString('h1Tip')
    }
    if(current.subType === 'h2') {
      current.tip = Helper.getString('h2Tip')
    }
  }

  renderLabel(current, options) {
    this._preFillTip(current)
    return (
      <div className={css(Styles.labelContainer)}>
        <div className={(this.props.formDefinition.formType === 'filter') ? css([Styles.labelInnerContainer,Styles.filterLabelInnerContainer]) : css(Styles.labelInnerContainer)}>
          {/* <p className={css(Styles.label)}>{current.label}{options && options.required && <span className={css(Styles.requiredIndicator)}>*</span>}</p> */}
          <p className={css(Styles.label)}>{current.label}</p>
          {options && options.required && <img className={css(Styles.requiredIndicator)} height='7' width='7' src={require('../../assets/images/emergency.svg')} />}
          {current.tip &&
          <Tooltip title={current.tip} classes={{ popper: css(Styles.tooltip) }}>
            <i className='material-icons-outlined' style={{ fontSize: 14, color: '#7d7d7d', cursor: 'pointer', marginLeft: 5, marginTop: 0, alignSelf: 'center' }}>info</i>
          </Tooltip>
          }
          {current.tipLink &&
          <a href={current.tipLink.src} target='_blank' rel='noopener noreferrer' className={css(Styles.link)}>{current.tipLink.title}</a>
          }

          {(this.props.formDefinition.formType === 'filter') && current.operators && (<Select
            value={current.selectedOperator || current.defaultOperator}
            open={this.state.filterOperatorOpenModalKey === current.key}
            onOpen={() => this.setState({ filterOperatorOpenModalKey: current.key })}
            onClose={()=> this.setState({ filterOperatorOpenModalKey: null })}
            onChange={(event)=>{
              this.handleFilterOperatorChange(event,current)
              this.setState({ filterOperatorOpenModalKey: null })
            }}
            disableUnderline
            MenuProps={{
              disablePortal: true,
              getContentAnchorEl: null,
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'center',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'center',
              },
              classes: { paper: css(Styles.isEqualSelectMenu) },
            }}
            classes={{ select: css(Styles.isEqualSelection) }}
            className={css(Styles.isEqualSelect)}
          >
            {current.operators.map((option) => (
              <MenuItem key={option.key.toString()} value={option.label.toString()}>
                {option.label}
              </MenuItem>
            ))}
          </Select>)}
        </div>
      </div>
    )
  }

  // Following function can be used to call submit function when user presses enter on form inputs
  // But we need to fix the behaviour in cases where another dialog comes over form dialog, hence commenting this code
  // keyActions(e) {
  //   if (!e) return
  //   const key = e.key
  //   const tagName = e.target.tagName

  //   if (key && key === 'Enter') {
  //     const ariaLabel = e.target.getAttribute('aria-label')
  //     if (tagName && (tagName === 'TEXTAREA' || (tagName === 'DIV' && ariaLabel === 'rdw-editor'))) return
  //     e.preventDefault()
  //     this.doAction()
  //   }
  //   if (key && key === 'Escape' && this.props.onClosed) this.props.onClosed()
  // }

  setStateKeyValue(options) {
    this.setState({ [options.key]: options.value });
  }

  collectPreviewFormData() {
    const data = {}
    for (const field of this.fields) {
      if(field.linkName) {
        data[field.linkName] = this.state[field.linkName]
      } else {
        data[field.key] = this.state[field.key]
      }
    }
    applyFlatListFieldsToFormData(data, this.fields, this.state, (field) => this.isConditionMatch(field))
    const prev = this._lastPreviewFormData
    if (prev) {
      const prevKeys = Object.keys(prev)
      const nextKeys = Object.keys(data)
      if (prevKeys.length === nextKeys.length) {
        let same = true
        for (const k of nextKeys) {
          if (prev[k] !== data[k]) { same = false; break }
        }
        if (same) return prev
      }
    }
    this._lastPreviewFormData = data
    return data
  }

  buildPreviewContext() {
    if (!this._previewContext || this._previewContext.formDefinition !== this.props.formDefinition) {
      this._previewContext = {
        formDefinition: this.props.formDefinition,
      }
    }
    return this._previewContext
  }

  renderPreviewPane() {
    if (!this.isPreviewActive()) return null
    const preview = this.props.formDefinition.preview
    return (
      <FormPreviewPane
        config={preview}
        formData={this.collectPreviewFormData()}
        context={this.buildPreviewContext()}
      />
    )
  }

  render() {
    if(this.props.formDefinition.formType === 'info') {
      return(
        <div className={css(Styles.containerNarrow)}>
          <Grid container spacing={4}>
            <Grid item xs={12} sm={12}>
              <SubEntityCard
                renderFieldsRowwise={true}
                formDefinition={{steps: this.props.formDefinition.steps, tooltipFields: this.props.formDefinition.tooltipFields}}
                item={this.processValuesBeforeAction() || {}}
                displayFieldActions={this.props.displayFieldActions}
                noEdit={true}
              />
            </Grid>
          </Grid>
        </div>
      )
    }
    const leftFields = [];
    const rightFields = [];
    const accountsLeftFields = [];
    const accountsRightFields = [];
    const accountsFullWidthFields = [];
    const advancedLeftFields = [];
    const advancedRightFields = [];
    const fullWidthFields = [];
    const advancedFullWidthFields = [];
    this.generateFields(leftFields, rightFields, accountsLeftFields, accountsRightFields, accountsFullWidthFields, advancedLeftFields, advancedRightFields, fullWidthFields, advancedFullWidthFields)
    const hasPreview = this.isPreviewActive()
    const fullWidthFieldsFirst = !!this.props.fullWidthFieldsFirst
    const mainFields = fullWidthFieldsFirst
      ? this.renderMainFieldsWithFullWidthFirst(fullWidthFields, leftFields, rightFields)
      : (
        <>
          {this.generateContent(leftFields, rightFields)}
          {this.renderFullWidthFieldGrid(fullWidthFields)}
        </>
      )
    const formContent = (
      <form key='form' autoComplete='off' className={css(Styles.form, hasPreview && Styles.formWithPreview)}>
        {mainFields}
        {(accountsLeftFields.length > 0 || accountsRightFields.length > 0 || accountsFullWidthFields.length > 0) && this.generateCollapsibleSection({
          title: Helper.getString('accounts') || 'Accounts',
          stateKey: 'isAccountsOpen',
          leftFields: accountsLeftFields,
          rightFields: accountsRightFields,
          fullWidthFields: accountsFullWidthFields,
          fullWidthKeyPrefix: 'list-fw-accounts',
        })}
        {(advancedLeftFields.length > 0 || advancedRightFields.length > 0 || advancedFullWidthFields.length > 0) && this.generateCollapsibleSection({
          title: Helper.getString('advanced') || 'Advanced',
          stateKey: 'isAdvancedOpen',
          leftFields: advancedLeftFields,
          rightFields: advancedRightFields,
          fullWidthFields: advancedFullWidthFields,
          fullWidthKeyPrefix: 'list-fw-advanced',
        })}
        <div className={css(Styles.actionsContainer)}>{this.generateActions()}</div>
      </form>
    );
    if (hasPreview) {
      return (
        <div className={css(Styles.previewLayout)}>
          <div className={css(Styles.previewLayoutFormPane)}>
            {formContent}
          </div>
          {this.renderPreviewPane()}
        </div>
      );
    }
    return formContent;
  }

  generateDeleteField() {
    const formControlParams = {};
    const current = {
      key: 'delete',
      label: 'Type "delete" for deletion',
    }
    const fieldParams = {
      id: current.key,
      value: this.state[current.key],
      fullWidth: true
    };

    return (
      <div className={(css(Styles.delFieldContainer))} key={current.key}>
        <FormControl className={css(Styles.formControlMain)} fullWidth {...formControlParams} >
          {this.renderLabel(current, formControlParams)}
          <TextField
            error={formControlParams.error}
            required={formControlParams.required}
            autoComplete='off'
            className={css(Styles.formControl)}
            {...fieldParams}
            InputProps={{
              disableUnderline: true,
              classes: {
                root: css(Styles.textInputStyles),
                input: css(Styles.formInputStyle),
              }
            }}
            onChange={this.handleDeleteFieldChange} />
        </FormControl>
      </div>
    )
  }

  renderFullWidthFieldGrid(fields, keyPrefix = 'list-fw', containerStyle) {
    if (!fields || fields.length === 0) {
      return null
    }
    return (
      <Grid container spacing={2} className={containerStyle ? css(containerStyle) : undefined} style={{ width: '100%' }}>
        {fields.map((item, idx) => (
          <Grid item xs={12} key={`${keyPrefix}-${idx}`}>
            {item}
          </Grid>
        ))}
      </Grid>
    )
  }

  // Keep full-width + left/right fields in one Grid so widths/padding align
  // (e.g. number-range Preset Range above conditional Start/End).
  renderMainFieldsWithFullWidthFirst(fullWidthFields, leftFields, rightFields) {
    const hasFullWidth = Array.isArray(fullWidthFields) && fullWidthFields.length > 0
    const hasLeft = Array.isArray(leftFields) && leftFields.length > 0
    const hasRight = Array.isArray(rightFields) && rightFields.length > 0
    if (!hasFullWidth && !hasLeft && !hasRight) {
      return null
    }

    const left = [...(leftFields || [])]
    const right = [...(rightFields || [])]
    const rowItems = []
    if (hasLeft && hasRight) {
      const lMax = Math.max(left.length, right.length)
      while (left.length < lMax) left.push('')
      while (right.length < lMax) right.push('')
      for (let i = 0; i < lMax; i++) {
        rowItems.push({ key: `fwf-l-${i}`, xs: 12, sm: 6, node: left[i] })
        rowItems.push({ key: `fwf-r-${i}`, xs: 12, sm: 6, node: right[i] })
      }
    } else if (hasLeft) {
      left.forEach((item, i) => rowItems.push({ key: `fwf-l-${i}`, xs: 12, node: item }))
    } else if (hasRight) {
      right.forEach((item, i) => rowItems.push({ key: `fwf-r-${i}`, xs: 12, node: item }))
    }

    return (
      <Grid container spacing={2} style={{ width: '100%' }}>
        {(fullWidthFields || []).map((item, idx) => (
          <Grid item xs={12} key={`list-fw-${idx}`}>
            {item}
          </Grid>
        ))}
        {rowItems.map((item) => (
          <Grid item xs={item.xs} sm={item.sm} key={item.key}>
            {item.node}
          </Grid>
        ))}
      </Grid>
    )
  }

  generateFields(leftFields, rightFields, accountsLeftFields, accountsRightFields, accountsFullWidthFields, advancedLeftFields, advancedRightFields, fullWidthFields, advancedFullWidthFields) {
    this.syncSyntheticOptionsForSelectLikeFields()
    for (const current of this.fields) {
      const formControlParams = {};
      let field;
      let errorTag = '';

      this.setFormControlParams(current, formControlParams);
      if (!current.valid) {
        errorTag = <FormHelperText className={css(Styles.errorMsg)}>{current._minMaxErr || (current.v_required && current.v_required.err) || ''}</FormHelperText>;
      }
      if(current.hidden) {
        continue
      }
      if(current && current.condition && current.condition.clearDefaultValue) {
        current.defaultValue = null
      }
      if (!this.isConditionMatch(current)) {
        continue
      }
      const resolved = this.resolveFieldForOperator(current)
      switch (resolved.type) {
      case 'document':
      case 'edit_text':
        if(resolved.subType === 'image') {
          field = this.generateImageUploader(resolved, formControlParams, errorTag)
        } else if(resolved.subType === 'document') {
          field = this.generateDoucmentUploader(resolved, formControlParams, errorTag)
        } else if(resolved.subType === 'colour') {
          this.isColourPickerPresent = true
          field = this.generateColourPicker(resolved, formControlParams, errorTag)
        } else {
          field = this.generateEditText(resolved, formControlParams, errorTag)
        }
        break;
      case 'rich_text': {
        const tf = parseRichTextToolbarFlags(resolved.toolbarFlags)
        if (!tf || tf.has('FONT_COLOR')) {
          this.isColourPickerPresent = true
        }
        field = this.generateRichtext(resolved, formControlParams, errorTag)
        break
      }
      case 'html':
        field = this.generateHtmlField(resolved, formControlParams, errorTag)
        break
      case 'radio':
      case 'check_box':
      case 'select':
        field = this.generateAutoCompleteSelection(resolved, formControlParams, errorTag)
        if (resolved.fullWidth) {
          if (resolved.accounts) {
            accountsFullWidthFields.push(field)
          } else if (resolved.advanced) {
            advancedFullWidthFields.push(field)
          } else {
            fullWidthFields.push(field)
          }
          continue
        }
        break;
      case 'dateTime':
        field = this.generateDateTimePicker(resolved, formControlParams, errorTag)
        break
      case 'date_yyyymmdd':
        field = this.generateDatePicker(resolved, formControlParams, errorTag, 'YYYYMMDD')
        break
      case 'date':
        field = this.generateDatePicker(resolved, formControlParams, errorTag)
        break
      case 'year':
        field = this.generateYearPicker(resolved, formControlParams, errorTag)
        break
      case 'time':
        field = this.generateTimePicker(resolved, formControlParams, errorTag)
        break
      case 'dateRange':
        field = this.generateDateRangePicker(resolved, formControlParams, errorTag)
        break
      case 'numberRange':
        field = this.generateNumberRangePicker(resolved, formControlParams, errorTag)
        break
      case 'duration':
        field = this.generateDurationPicker(resolved, formControlParams, errorTag)
        break
      case 'list': {
        const listField = this.generateList(resolved, formControlParams, errorTag)
        if (resolved.accounts) {
          accountsFullWidthFields.push(listField)
        } else if (resolved.advanced) {
          advancedFullWidthFields.push(listField)
        } else {
          fullWidthFields.push(listField)
        }
        continue
      }
      default:
        console.error(Helper.getString('unknownFieldType'), resolved.type)
        continue;
      }
      if (!resolved.placement) {
        resolved.placement = 'left' // default
      }
      if (resolved.accounts) {
        if (resolved.placement === 'left') {
          accountsLeftFields.push(field);
        } else if (resolved.placement === 'right') {
          accountsRightFields.push(field);
        }
      } else if(resolved.advanced) {
        if (resolved.placement === 'left') {
          advancedLeftFields.push(field);
        } else if (resolved.placement === 'right') {
          advancedRightFields.push(field);
        }
      } else {
        if (resolved.placement === 'left') {
          leftFields.push(field);
        } else if (resolved.placement === 'right') {
          rightFields.push(field);
        }
      }
    }
  }

  generateContent(leftFields, rightFields) {
    if ((!leftFields || leftFields.length === 0) && (!rightFields || rightFields.length === 0)) {
      return null
    }
    if (leftFields.length !== 0 && rightFields.length === 0) {
      return (
        <Grid container spacing={2}>
          {leftFields.map(item => {
            return (
              <Grid item xs={12}>
                {item}
              </Grid>
            )
          })
          }
        </Grid>
      )
    } else if (rightFields.length !== 0 && leftFields.length === 0) {
      return (
        <Grid container spacing={2}>
          {rightFields.map(item => {
            return (
              <Grid item xs={12}>
                {item}
              </Grid>
            )
          })
          }
        </Grid>
      )
    }

    const combinedArray = []
    const lMin = Math.min(leftFields.length, rightFields.length);
    const lMax = Math.max(leftFields.length, rightFields.length);
    if (lMin === leftFields.length && lMin !== rightFields.length) {
      for (let i = 0; i < lMin - rightFields.length; i++) {
        leftFields.push('')
      }
    } else if (lMin === rightFields.length && lMin !== leftFields.length) {
      for (let i = 0; i < lMin - leftFields.length; i++) {
        rightFields.push('')
      }
    }
    for (let i = 0; i < lMax; i++) {
      combinedArray.push(leftFields[i], rightFields[i]);
    }

    // combinedArray.push(...leftFields.slice(l), ...rightFields.slice(l));

    return (
      <Grid container spacing={2}>
        {combinedArray.map(item => {
          return (
            <Grid item xs={12} sm={6} md={6} lg={6}>
              {item}
            </Grid>
          )
        })
        }
      </Grid>
    )
  }

  generateCollapsibleSection({ title, stateKey, leftFields, rightFields, fullWidthFields = [], fullWidthKeyPrefix = 'list-fw-section' }) {
    const isOpen = this.state[stateKey]
    return (
      <div>
        <Accordion
          elevation={0}
          expanded={isOpen}
          onChange={() => this.setState((prev) => ({
            [stateKey]: !prev[stateKey],
          }))}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon className={css(Styles.accordionIcon)}/>}
            className={css(Styles.accordionSummary)}
          >
            <Typography className={css(Styles.advancedTitle)}>{title}</Typography>
          </AccordionSummary>
          <AccordionDetails className={css(Styles.accordionDetails)}>
            {this.generateContent(leftFields, rightFields)}
            {this.renderFullWidthFieldGrid(fullWidthFields, fullWidthKeyPrefix)}
          </AccordionDetails>
        </Accordion>
      </div>
    )
  }

  generateContentOld(leftFields, rightFields) {
    if (rightFields.length > 0) {
      return <div>
        <Grid container>
          <Grid className={css(Styles.leftGridPadding)} item xs={12} sm={6}>
            {leftFields}
          </Grid>
          <Grid className={css(Styles.rightGridPadding)} item xs={12} sm={6}>
            {rightFields}
          </Grid>
        </Grid>
      </div>
    } else {
      return <div>
        <Grid container>
          <Grid item xs={12}>
            {leftFields}
          </Grid>
        </Grid>
      </div>
    }
  }

  generateActions() {
    let buttonText = this.resolveCtaButtonText() || 'Delete'
    let buttonStyle = Styles.buttonDelete
    let buttonTextStyle = Styles.buttonText
    if (Helper.inArray(['add', 'edit', 'filter'], this.props.formDefinition.formType)) {
      buttonText = this.resolveCtaButtonText() || 'Save'
      buttonStyle = Styles.button
    } else if ('approve' === this.props.formDefinition.formType) {
      buttonText = 'Approve'
      buttonStyle = Styles.button
    }
    const isButtonDisabled = this.props.formDefinition.disableCTAButton || false

    if(isButtonDisabled) {
      buttonStyle = Styles.buttonDisabled
      buttonTextStyle = Styles.buttonDisabledText
    }

    const dialogAction = <div className={css(Styles.actions)}>
      <span className={this.state.success ? css(Styles.successMsg) : css(Styles.errorMsg)}>
        <span dangerouslySetInnerHTML={{ __html: Helper.renderHtmlOrPlainText(this.state.message) }}></span>
      </span>
      {(this.props.formDefinition.formType === 'del' || this.props.formDefinition.formType === 'delete') &&
        !this.props.formDefinition.skipDeleteConfirmInput &&
        this.generateDeleteField()
      }
      <AppButton
        buttonStyle={buttonStyle}
        buttonTextStyle={buttonTextStyle}
        disabled={this.state.inProcess || isButtonDisabled}
        inProcess={this.state.inProcess}
        onClick={this.doAction}
        buttonText={buttonText}
      />
    </div>

    return dialogAction
  }

  componentDidMount() {
    for (const current of this.fields) {
      if (current.pickLoc) {
        this.initGooglePlaces(current)
      }
    }
    this.getColors()
    this.syncDescriptionConditionMatch()
  }

  componentDidUpdate() {
    this.syncDescriptionConditionMatch()
  }

  syncDescriptionConditionMatch() {
    const condition = this.props.formDefinition.descriptionCondition
    if (!condition || !this.props.onDescriptionConditionChange) return
    const isMatch = this.isConditionMatch({ condition })
    if (isMatch !== this.descriptionConditionMatch) {
      this.descriptionConditionMatch = isMatch
      this.props.onDescriptionConditionChange(isMatch)
    }
  }

  getColors() {
    this.setState({colorPickerLoading:true})

    ApiHelper.call({
      authWithKey: true,
      method: 'GET',
      paths:['miscs','appconfig'],
      queryParams: {
        orgId:this.currOrgId
      }
    },function(err,data) {
      if(err) {
        console.log(err,'LOADING_ERR_COLORS');
        this.setState({
          colorPickerLoading:false,
        })
        return
      }
      this.setState({
        colorPickerLoading:false,
        brandColors: (data.__appConfig && data.__appConfig.colourhistory) || []
      })
    }.bind(this))
  }

  initGooglePlaces(current) {
    const inputNode = document.getElementById(current.key);
    const autoComplete = new window.google.maps.places.Autocomplete(inputNode);
    autoComplete.setFields(['address_component', 'adr_address', 'name', 'formatted_address', 'geometry'])
    autoComplete.addListener('place_changed', () => {
      const place = autoComplete.getPlace();
      this.locations[current.locName] = {
        name: place.name,
        address: place.formatted_address,
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng()
      }
      this.setState({
        [current.key]: place.formatted_address
      })
      // console.log('place:', place);
      //console.log(place.geometry.location.lat());
      //console.log(place.geometry.location.lng());
    });
  }

  generateRichtext(current, formControlParams, errorTag) {
    const tf = parseRichTextToolbarFlags(current.toolbarFlags)
    const hasTf = tf != null
    return (
      <div key={current.key}>
        {this.renderLabel(current, formControlParams)}
        <RichtextEditor
          current={current}
          fullEditor={current.fullEditor}
          colorPicker={hasTf ? tf.has('FONT_COLOR') : current.colorPicker}
          variablePicker= {current.variablePicker}
          value={this.state[current.key]}
          disabled={this.shouldDisable(current)}
          stripPastedStyles={false}
          stripPastedImages={true}
          clearFormatting={hasTf ? tf.has('CLEAR_FORMATTING') : true}
          handleRichtextInputChange={this.handleRichtextInputChange}
          colorPickerData={{isLoading:this.state.colorPickerLoading,data:this.state.brandColors}}
          handleColorChange={this.handleColorChange} />
        {errorTag}
      </div>
    )
  }
  generateDurationPicker(current, formControlParams, errorTag) {
    const rawMs = this.state[current.key]
    const subtype = current.subType || current.subtype || 'relative'
    const directionDisabled = !!current.directionDisabled
    let initialMs = null
    if (rawMs !== undefined && rawMs !== null && rawMs !== '') {
      const n = Number(rawMs)
      if (!Number.isNaN(n)) initialMs = n
    }
    return (
      <div key={current.key}>
        <FormControl className={css(Styles.formControlMain)} fullWidth {...formControlParams}>
          {this.renderLabel(current, formControlParams)}
          <ScheduleDurationPicker
            initialMs={initialMs}
            subtype={subtype}
            directionDisabled={directionDisabled}
            allowedUnits={Array.isArray(current.allowedUnits) && current.allowedUnits.length > 0 ? current.allowedUnits : undefined}
            disabled={current.disabled || this.shouldDisable(current)}
            onDurationChange={(ms) => this.onDurationChange(current, ms)}
          />
          {errorTag}
        </FormControl>
      </div>
    )
  }

  onDurationChange = (current, ms) => {
    if (!ms && ms !== 0 && ms !== '0') {
      return this.setFieldValueAndResetDependents(current.key, null)
    }
    this.setFieldValueAndResetDependents(current.key, ms)
  }
  renderImageUploader(current) {
    return (
      <Fragment>
        {this.props.customFormComponents && this.props.customFormComponents.imageUpload ?
          <this.props.customFormComponents.imageUpload.comp
            uploadId={current.key}
            url={current.link}
            onUploadSuccess={this.handleUploadSuccess.bind(this)}
            width={current.imageWidth}
            height={current.imageHeight}
            uploadType={current.uploadType}
            uploadAccept={current.uploadAccept}
            uploadLocation={this.props.uploadLocation}
            orgId={this.props.orgId}
            uploadExt={current.uploadExt}
            uploadMs={current.uploadMs || this.props.uploadMs}
            mode={current.mode}
            borderRadius={0}
            disabled={this.shouldDisable(current)}
            disableDeletion={current.disableDeletion}
            {...this.props.customFormComponents.imageUpload.props}
          />
          :
          <ImageUploader
            uploadId={current.key}
            url={current.link}
            onUploadSuccess={this.handleUploadSuccess.bind(this)}
            width={current.imageWidth}
            height={current.imageHeight}
            uploadType={current.uploadType}
            uploadAccept={current.uploadAccept}
            uploadLocation={this.props.uploadLocation}
            orgId={this.props.orgId}
            uploadExt={current.uploadExt}
            uploadMs={current.uploadMs || this.props.uploadMs}
            mode={current.mode}
            borderRadius={0}
            disableDeletion={current.disableDeletion}
            disabled={this.shouldDisable(current)}
            setStateKeyValueHandler={this.setStateKeyValue.bind(this)}
            autoFillTitleKey={current.autoFillTitleKey || ''}
          />
        }
      </Fragment>
    )
  }

  generateHtmlField(current, formControlParams, errorTag) {
    const htmlContent = current.htmlContent || this.state[current.key] || ''

    return <div key={current.key}>
      <FormControl className={css(Styles.formControlMain)} fullWidth {...formControlParams}>
        {this.renderLabel(current, formControlParams)}
        <div
          className={css(Styles.htmlFieldContent)}
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
        {errorTag}
      </FormControl>
      {this.generatePendingApproval(current)}
    </div>
  }

  generateEditText(current, formControlParams, errorTag) {
    const listBind = current._listBind
    const fieldParams = {
      id: current.key,
      value: listBind ? (listBind.value == null ? '' : listBind.value) : this.state[current.key],
      fullWidth: true
    };
    if (current.multiline) {
      fieldParams.multiline = current.multiline;
      fieldParams.rows = current.rows || 5;
    }
    fieldParams.disabled = this.shouldDisable(current)

    if (
      this.props.customFormComponents &&
      this.props.customFormComponents.edit_text &&
      this.props.customFormComponents.edit_text.fieldsToEnableCustomComp.includes(current.key)
    ) {
      return <div key={current.key}>
        <FormControl className={css(Styles.formControlMain)} fullWidth {...formControlParams}>
          {this.renderLabel(current, formControlParams)}
          <this.props.customFormComponents.edit_text.comp
            orgId={this.currOrgId}
            // mode={current.mode}
            // borderRadius={0}
            disabled={this.shouldDisable(current)}
            disableDeletion={current.disableDeletion}
            current={current}
            formControlParams={formControlParams}
            fieldParams={fieldParams}
            onChange={this.handleInputChange}
            allKeysData={{...this.state}}
            // {...this.props} //why passing all?
            formDefinition={this.props.formDefinition}
            {...this.props.customFormComponents.edit_text.props}
          />
          {this.generatePendingApproval(current)}
        </FormControl>
      </div>
    }


    return <div key={current.key}>
      <FormControl className={css(Styles.formControlMain)} fullWidth {...formControlParams} >
        {this.renderLabel(current, formControlParams)}
        {(!current.imageUpload && !current.documentUpload && !current.colourPicker) && //retained for backward compatibility
          <TextField
            error={formControlParams.error}
            required={formControlParams.required}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !fieldParams.multiline) {
                event.preventDefault();
              }
            }}
            variant={'standard'}
            className={css(Styles.formControl)}
            InputProps={{
              disableUnderline: true,
              type: current.subType === 'password' ? 'password' : 'text',
              classes: {
                root: this.shouldDisable(current) ? css([Styles.textInputStyles, Styles.textInputStylesDisabled]) : css(Styles.textInputStyles),
                input: css(Styles.formInputStyle, Styles.textInput),
              }
            }}
            autoComplete='off'
            {...fieldParams}
            onChange={listBind ? (e) => listBind.onChange(e.target.value) : this.handleInputChange} />
        }
        {current.imageUpload && //retained for backward compatibility
          this.renderImageUploader(current)
        }
        {current.documentUpload && //retained for backward compatibility
          <DocumentUploader
            label={current.label}
            uploadId={current.key}
            link={current.link}
            uploadType={current.uploadType}
            uploadAccept={current.uploadAccept}
            uploadLocation={this.props.uploadLocation}
            uploadExt={current.uploadExt}
            uploadMs={current.uploadMs || this.props.uploadMs}
            onUploadSuccess={this.handleUploadSuccess.bind(this)}
            value={listBind ? listBind.value : this.state[current.key]}
            disabled={this.shouldDisable(current)}
            fileSizeLimit={current.fileSizeLimit}
          />
        }
        {current.colourPicker && //retained for backward compatibility
          <ColourPicker
            defaultValue={current.defaultValue}
            colorPickerData={{isLoading:this.state.colorPickerLoading,data:this.state.brandColors}}
            id={current.key}
            value={this.state[current.key]}
            handleColorChange={this.handleColorChange} />
        }
        {errorTag}
      </FormControl>
      {this.generatePendingApproval(current)}
    </div>
  }

  generateImageUploader(current, formControlParams, errorTag) {
    const fieldParams = {
      id: current.key,
      value: this.state[current.key],
      fullWidth: true
    };
    if (current.multiline) {
      fieldParams.multiline = current.multiline;
      fieldParams.rows = current.rows || 5;
    }
    fieldParams.disabled = this.shouldDisable(current)

    return <div key={current.key}>
      <FormControl className={css(Styles.formControlMain)} fullWidth {...formControlParams} >
        {this.renderLabel(current, formControlParams)}
        {this.renderImageUploader(current)}
        {errorTag}
      </FormControl>
      {this.generatePendingApproval(current)}
    </div>
  }

  generateDoucmentUploader(current, formControlParams, errorTag) {
    const fieldParams = {
      id: current.key,
      value: this.state[current.key],
      fullWidth: true
    };
    if (current.multiline) {
      fieldParams.multiline = current.multiline;
      fieldParams.rows = current.rows || 5;
    }
    fieldParams.disabled = this.shouldDisable(current)

    return <div key={current.key}>
      <FormControl className={css(Styles.formControlMain)} fullWidth {...formControlParams} >
        {this.renderLabel(current, formControlParams)}
        <DocumentUploader
          label={current.label}
          uploadId={current.key}
          link={current.link}
          forceAllowFileOpen={current.forceAllowFileOpen}
          uploadType={current.uploadType}
          uploadAccept={current.uploadAccept}
          uploadLocation={this.props.uploadLocation}
          uploadExt={current.uploadExt}
          uploadMs={current.uploadMs || this.props.uploadMs}
          onUploadSuccess={this.handleUploadSuccess.bind(this)}
          value={this.state[current.key]}
          disabled={this.shouldDisable(current)}
        />
        {errorTag}
      </FormControl>
      {this.generatePendingApproval(current)}
    </div>
  }

  generateColourPicker(current, formControlParams, errorTag) {
    const fieldParams = {
      id: current.key,
      value: this.state[current.key],
      fullWidth: true
    };
    if (current.multiline) {
      fieldParams.multiline = current.multiline;
      fieldParams.rows = current.rows || 5;
    }
    fieldParams.disabled = this.shouldDisable(current)

    return <div key={current.key}>
      <FormControl className={css(Styles.formControlMain)} fullWidth {...formControlParams} >
        {this.renderLabel(current, formControlParams)}
        <ColourPicker
          defaultValue={current.defaultValue}
          colorPickerData={{isLoading:this.state.colorPickerLoading,data:this.state.brandColors}}
          id={current.key}
          value={this.state[current.key]}
          handleColorChange={this.handleColorChange} />
        {errorTag}
      </FormControl>
      {this.generatePendingApproval(current)}
    </div>
  }

  generateDateTimePicker(current, formControlParams, errorTag) {
    return (
      <Fragment key={current.key}>
        <MuiPickersUtilsProvider utils={MomentUtils} moment={moment}>
          {this.renderLabel(current, formControlParams)}
          <div className={css(Styles.datePickerContainer)}>
            <DateTimePicker
              keyboard
              variant={'standard'}
              keyboardIcon={<i className='material-icons-outlined' >calendar_today</i>}
              format='DD/MM/YYYY hh:mm A'
              placeholder='DD/MM/YYYY hh:mm A'
              value={this.state[current.key] ? Number.parseInt(this.state[current.key]) : null}
              onBlur={(e) => {
                if (!e.target.value) {
                  this.setState({ [current.key]: null })
                }
              }}
              onChange={this.handleDateChange(current)}
              animateYearScrolling={false}
              clearable={current.clearable}
              margin='normal'
              fullWidth
              disabled={this.shouldDisable(current)}
              className={css(Styles.pickerStyle)}
              InputProps={{
                disableUnderline: true,
                classes: {
                  root: this.shouldDisable(current) ? css([Styles.pickerInputStyles, Styles.textInputStylesDisabled]) : css(Styles.pickerInputStyles),
                  input: css(Styles.formInputStyle),
                }
              }}
              DialogProps={{
                className: css(Styles.dialog),
                PaperProps: {
                  className: css(Styles.paper)
                }
              }}
              {...formControlParams}
            />
            {this.state[current.key] && (
              <IconButton
                size='small'
                disabled={this.shouldDisable(current)}
                onClick={() => this.setState({ [current.key]: null })}
                className={css(Styles.datePickerCloseBtn)}>
                <CloseIcon fontSize='small' />
              </IconButton>
            )}
          </div>
        </MuiPickersUtilsProvider>
        {errorTag}
      </Fragment>
    )
  }

  generateDatePicker(current, formControlParams, errorTag, format) {
    return (
      <Fragment key={current.key}>
        <MuiPickersUtilsProvider utils={MomentUtils} moment={moment} >
          {this.renderLabel(current, formControlParams)}
          <div className={css(Styles.datePickerContainer)}>
            <DatePicker
              variant={'standard'}
              keyboard
              keyboardIcon={<i className='material-icons-outlined' >calendar_today</i>}
              format='DD-MMM-YYYY'
              placeholder='DD-MMM-YYYY'
              value={this.state[current.key] || null}
              onBlur={(e) => {
                if (!e.target.value) {
                  this.setState({ [current.key]: null })
                }
              }}
              onChange={this.handleDateChange(current, format)}
              animateYearScrolling={false}
              clearable={current.clearable}
              margin='normal'
              fullWidth
              disabled={this.shouldDisable(current)}
              className={css(Styles.pickerStyle)}
              InputProps={{
                disableUnderline: true,
                classes: {
                  root: this.shouldDisable(current) ? css([Styles.pickerInputStyles, Styles.textInputStylesDisabled]) : css(Styles.pickerInputStyles),
                  input: css(Styles.formInputStyle)
                }
              }}
              DialogProps={{
                className: css(Styles.dialog),
                PaperProps: {
                  className: css(Styles.paper)
                }
              }}
              {...formControlParams}
            />
            {this.state[current.key] && (
              <IconButton
                size='small'
                disabled={this.shouldDisable(current)}
                onClick={() => this.setState({ [current.key]: null })}
                className={css(Styles.datePickerCloseBtn)}>
                <CloseIcon fontSize='small' />
              </IconButton>
            )}
          </div>
        </MuiPickersUtilsProvider>
        {errorTag}
      </Fragment>
    )
  }

  generateYearPicker(current, formControlParams, errorTag) {
    return (
      <Fragment key={current.key}>
        <MuiPickersUtilsProvider utils={MomentUtils} moment={moment}>
          {this.renderLabel(current, formControlParams)}
          <DatePicker
            variant={'standard'}
            views={['year']}
            keyboard
            keyboardIcon={<i className='material-icons-outlined' >calendar_today</i>}
            format='YYYY'
            placeholder='YYYY'
            value={this.state[current.key] || null}
            onChange={this.handleDateChange(current)}
            animateYearScrolling={false}
            clearable={current.clearable}
            margin='normal'
            fullWidth
            disabled={this.shouldDisable(current)}
            className={css(Styles.pickerStyle)}
            InputProps={{
              disableUnderline: true,
              classes: {
                root: this.shouldDisable(current) ? css([Styles.pickerInputStyles, Styles.textInputStylesDisabled]) : css(Styles.pickerInputStyles),
                input: css(Styles.formInputStyle),
              }
            }}
            DialogProps={{
              className: css(Styles.dialog),
              PaperProps: {
                className: css(Styles.paper)
              }
            }}
            {...formControlParams}
          />
        </MuiPickersUtilsProvider>
        {errorTag}
      </Fragment>
    )
  }

  generateTimePicker(current, formControlParams, errorTag) {
    return (
      <Fragment key={current.key}>
        <MuiPickersUtilsProvider utils={MomentUtils} moment={moment}>
          {this.renderLabel(current, formControlParams)}
          <TimePicker
            keyboard
            variant={'standard'}
            keyboardIcon={<i className='material-icons-outlined' >schedule</i>}
            placeholder='hh:mm A'
            value={this.state[current.key] || null}
            onChange={this.handleDateChange(current)}
            disableOpenOnEnter
            clearable={current.clearable}
            margin='normal'
            fullWidth
            disabled={this.shouldDisable(current)}
            className={css(Styles.pickerStyle)}
            InputProps={{
              disableUnderline: true,
              classes: {
                root: this.shouldDisable(current) ? css([Styles.pickerInputStyles, Styles.textInputStylesDisabled]) : css(Styles.pickerInputStyles),
                input: css(Styles.formInputStyle),
              }
            }}
            DialogProps={{
              className: css(Styles.dialog),
              PaperProps: {
                className: css(Styles.paper)
              }
            }}
            {...formControlParams}
          />
        </MuiPickersUtilsProvider>
        {errorTag}
      </Fragment>
    )
  }

  getDateRangeOptions(current) {
    if (this.state[current.key]) {
      const value = this.state[current.key]

      return {
        id: value.id,
        startDate: value.startValue,
        endDate: value.endValue
      }
    }

    return { id: 'all_time' }
  }

  generateDateRangePicker(current, formControlParams, errorTag) {
    const dateRangeOptions = this.getDateRangeOptions(current)
    return (
      <Fragment key={current.key}>
        {this.renderLabel(current, formControlParams)}
        <ReactDateRangeWrapper
          onDateRangeChange={(dateRangeValues)=>this.onDateRangeChange(current, dateRangeValues)}
          initialRange={dateRangeOptions}
          availableDateRanges={current && current.availableDateRanges}
          timezone={current && current.timezone}
          displayFieldStyle={Styles.dateRangePickerStyle}
          disabled={current && current.disabled}
          title={this.props.formDefinition.formType === 'filter' && current.label ? Helper.capitalizeFirstLetter(current.label) : undefined}
        />
        {errorTag}
      </Fragment>
    )
  }

  generateNumberRangePicker(current, formControlParams, errorTag) {
    if(this.state[current.key]) {
      this.numberRangeOptions = {
        startValue: this.state[current.key].startValue,
        endValue: this.state[current.key].endValue
      }
    }
    const rangeOptions = Array.isArray(current.options)
      ? current.options.filter((opt) => opt && (opt.from != null || opt.to != null))
      : []
    return <div key={current.key}>
      <FormControl className={css(Styles.formControlMain)} fullWidth {...formControlParams} >
        {this.renderLabel(current, formControlParams)}
        <NumberRangePicker
          title={current.label && Helper.capitalizeFirstLetter(current.label)}
          initialRange={this.numberRangeOptions}
          rangeOptions={rangeOptions}
          onNumberRangeChange={(numberRangeValues)=>this.onNumberRangeChange(current, numberRangeValues)}
        />
        {errorTag}
      </FormControl>
      {this.generatePendingApproval(current)}
    </div>
  }

  handleDateChange = (current, format) => date => {
    current.valid = true;
    if (!date) {
      return this.setFieldValueAndResetDependents(current.key, null);
    }
    this.setFieldValueAndResetDependents(current.key, format ? date.format(format) : date.utc().valueOf());
  }

  onDateRangeChange = (current, dateRangeValues) => {
    if (!dateRangeValues || !dateRangeValues.id || (!dateRangeValues.startDate && !dateRangeValues.endDate) || dateRangeValues.id === 'all_time') {
      return this.setFieldValueAndResetDependents(current.key, null);
    }

    if(current && dateRangeValues) {
      this.setFieldValueAndResetDependents(current.key, {
        id: dateRangeValues.id,
        type: current.type,
        subType: current.subType,
        startValue: dateRangeValues.startDate,
        endValue: dateRangeValues.endDate
      });
    }
  }

  onNumberRangeChange = (current, numberRangeValues) => {
    if (!numberRangeValues || (!numberRangeValues.startValue && !numberRangeValues.endValue)) {
      return this.setFieldValueAndResetDependents(current.key, null);
    }
    if(current && numberRangeValues) {
      this.setFieldValueAndResetDependents(current.key, {
        type: current.type,
        startValue: numberRangeValues.startValue,
        endValue: numberRangeValues.endValue
      })
    }
  }

  generateSelection(current, formControlParams, errorTag) {
    const options = [];
    for (const option of current.options) {
      let cbOption = '';
      if (current.multiSelect) {
        cbOption = <Checkbox color='primary' checked={this.state[current.key].indexOf(option.key) > -1} />
      }
      options.push(
        <MenuItem key={option.key} value={option.key}>
          {cbOption}
          {option.text || option.label}
        </MenuItem>
      );
    }
    let renderParams = {};
    if (current.multiSelect) {
      const searchOptions = current.options;
      renderParams = {
        renderValue: selected => (
          <div className={css(Styles.chips)}>
            {selected.map(value => { return this.findValueInList(value, searchOptions) })}
          </div>
        )
      }
    }
    return <FormControl className={css(Styles.formControlMain)} key={current.key} fullWidth {...formControlParams} variant='outlined'>
      <InputLabel htmlFor={current.label}>{current.label}</InputLabel>
      <Select
        multiple={current.multiSelect}
        value={this.state[current.key] ? this.state[current.key] : current.value}
        onChange={this.handleSelectChange}
        {...renderParams}
        input={<OutlinedInput name={current.key} id={current.key} disabled={this.shouldDisable()} label={current.label} />}
      >
        {options}
      </Select>
      {errorTag}
      {this.generatePendingApproval(current)}
    </FormControl>
  }

  findValueInList(value, searchOptions) {
    const found = searchOptions.find(o => o.key === value)
    if (found) {
      return (<Chip key={value} label={found.text || found.label} />)
    }
  }

  generateAutoCompleteSelection(current, formControlParams, errorTag) {
    const value = current._listBind ? this.processValueToPreFillForListBind(current) : this.processValueToPreFill(current)
    const hasValue = Array.isArray(value) ? value.length > 0 : !!value
    const filter = createFilterOptions();
    const fieldParams = {
      fullWidth: true,
      multiple: current.multiSelect,
      options: Array.isArray(current.options) ? current.options : [],
      value: value,
    };
    fieldParams.disabled = this.shouldDisable(current)
    const formType = this.props.formDefinition && this.props.formDefinition.formType
    const isEntityAddEditForm = formType === 'add' || formType === 'edit'

    if (isEntityAddEditForm) {
      fieldParams.openOnFocus = true
      fieldParams.filterOptions = (options, params) =>
        Helper.buildEntityFormAutocompleteFilterOptions(options, params, {
          addOptions: current.addOptions === true,
          addOptionsFormatter: current.addOptionsFormatter,
          formatAddOptionInput: Helper.formatAddOptionInput,
          filterOptionsFn: filter,
          selectedValue: value,
          multiSelect: current.multiSelect,
        })
    } else if (current.addOptions === true) {
      fieldParams.filterOptions = (options, params) => {
        const filtered = filter(options, params);
        const { inputValue } = params;
        const formattedValue = Helper.formatAddOptionInput(inputValue, current.addOptionsFormatter)
        const isExisting = options.some((option) => {
          const optLabel = option.text || option.label || option.key
          return inputValue === optLabel || formattedValue === optLabel || formattedValue === option.key
        });
        if (inputValue !== '' && formattedValue && !isExisting) {
          filtered.unshift({
            value: formattedValue,
            text: `Add "${formattedValue}"`,
          });
        }
        return filtered;
      }
    }

    // Enable adding option dynamically (filter dialog / non-entity forms)
    if (current.addOptions === true) {
      fieldParams.closeIcon = null
      fieldParams.loading = this.state.formFieldUpdateProgress !== current.key
      fieldParams.loadingText = Helper.getString('optionListLoadingText')
    }
    const selectionBody = (
      <FormControl className={css(Styles.formControlMain)} fullWidth {...formControlParams} >
        {this.renderLabel(current, formControlParams)}
        <Autocomplete
          {...fieldParams}
          onChange={(e, val) => {
            if (current._listBind) {
              const hasSynthetic = current.multiSelect
                ? Array.isArray(val) && val.some((o) => o && !o.key)
                : (val && !val.key)
              if (current.addOptions && hasSynthetic) {
                this.onListBindAutoCompleteChange(val, current)
                return
              }
              if (current.multiSelect) {
                const keys = Array.isArray(val)
                  ? val.map((item) => item && item.key).filter(Boolean)
                  : []
                current._listBind.onChange(keys)
              } else {
                current._listBind.onChange((val && val.key) || '')
              }
              return
            }
            this.onAutoCompleteChange(val, current)
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
            }
          }}
          getOptionLabel={(option) => {
            if (!option) return ''
            if (option.key) return option.label || option.text || String(option.key)
            return option.label || option.text || (option.value != null ? String(option.value) : '')
          }}
          isOptionEqualToValue={(option, val) => {
            if (option === val) return true
            if (!option || !val) return false
            if (option.key != null && val.key != null) return String(option.key) === String(val.key)
            return false
          }}
          ChipProps={{
            classes: current._listBind ? { root: css(Styles.listRowChip), label: css(Styles.listRowChipLabel), deleteIcon: css(Styles.listRowChipDeleteIcon) } : undefined,
            deleteIcon: <svg className={css(Styles.modalCloseIcon)} viewBox='0 0 36 36'>
              <path d='M28.5 9.62L26.38 7.5 18 15.88 9.62 7.5 7.5 9.62 15.88 18 7.5 26.38l2.12 2.12L18 20.12l8.38 8.38 2.12-2.12L20.12 18z'></path>
            </svg>
          }}
          renderOption={(option) => (
            <div className={(option && !option.key) ? css(Styles.addOptionStyle, Styles.healthOptionRow) : css(Styles.healthOptionRow)}>
              {option && option.healthColor && (
                <Tooltip title={(option.healthTooltip ? `${option.healthTooltip} (click for history)` : 'Click for history')} placement='top'>
                  <span
                    className={css(Styles.healthDot, Styles.healthDotClickable)}
                    style={{ backgroundColor: option.healthColor }}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      if (option.onHealthDotClick) {
                        option.onHealthDotClick(option, e)
                        return
                      }
                      const inboxId = option.inboxId || (option.key && String(option.key).includes('@') ? null : option.key)
                      const orgId = (this.props.apiData && this.props.apiData.orgId) || this.currOrgId
                      if (inboxId && orgId) {
                        const { CRMHelper } = require('../../helpers/CRMHelper')
                        CRMHelper.openInboxHealthHistory({
                          orgId,
                          inboxId,
                          email: option.inboxEmail || option.label || option.text || '',
                        })
                      }
                    }}
                    onMouseDown={(e) => {
                      // Prevent Autocomplete from selecting the option when clicking the dot
                      e.preventDefault()
                      e.stopPropagation()
                    }}
                  />
                </Tooltip>
              )}
              <span>{option.text || option.label}</span>
            </div>
          )}
          // renderOption={option => {
          //   return (
          //     <React.Fragment>
          //       {current.showThumbnail &&
          //         <React.Fragment>
          //           {option.img ?
          //             <div key={option.key} className={css(Styles.optionImageContainer, Styles.optionImageContainerSpec)}>
          //               <img width={20} height={20} src={option.img} className={css(Styles.optionImage)} />
          //             </div>
          //             :
          //             <div key={option.key} className={css(Styles.optionImageContainer, Styles.optionImageContainerSpec)}>
          //               <ImageIcon className={css(Styles.optionImage)} />
          //             </div>
          //           }
          //         </React.Fragment>
          //       }
          //       {option.label || option.text}
          //     </React.Fragment>
          //   )
          // }}
          classes={{
            tag: css(Styles.tag),
            popupIndicator: css(Styles.popupIndicator),
            popper: css(Styles.dialog),
            option: css(Styles.option),
            clearIndicator: hasValue ? css(Styles.clearIndicatorAlwaysVisible) : css(Styles.clearIndicatorHidden),
          }}
          renderInput={params =>
            <TextField
              error={formControlParams.error}
              required={formControlParams.required}
              {...params}
              variant={'standard'}
              className={css(Styles.formControl)}
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
                readOnly: (current.disableTyping && !isEntityAddEditForm) ? true : false,
                classes: {
                  root: this.shouldDisable(current) ? css([Styles.textInputStyles, Styles.textInputStylesDisabled, Styles.autoCompleteInputRoot]) : css([Styles.textInputStyles, Styles.autoCompleteInputRoot]),
                  input: css(Styles.formInputStyle, Styles.textInput),
                },
                endAdornment: (
                  <React.Fragment>
                    {(this.state.formFieldUpdateProgress && this.state.formFieldUpdateProgress === current.key) ? <CircularProgress className={css(Styles.optionLoaderSpinner)} color='inherit' size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
                // startAdornment: (
                //   current.showThumbnail &&
                //   <InputAdornment position='start' className={css(Styles.inputAdornment)}>
                //     {fieldParams.options.map((item, index) => {
                //       if (item.img && item.key === fieldParams.value.key) {
                //         return (
                //           <div key={item.key} className={css(Styles.optionImageContainer)}>
                //             <img width={20} height={20} src={item.img} className={css(Styles.optionImage)} />
                //           </div>
                //         )
                //       } else if (item.key === fieldParams.value.key) {
                //         return (
                //           <div key={item.key} className={css(Styles.optionImageContainer)}>
                //             <ImageIcon className={css(Styles.optionImage)} />
                //           </div>
                //         )
                //       }
                //     })}
                //     {params.InputProps.startAdornment}
                //   </InputAdornment>
                // )

              }} />}
        />
        {errorTag}
        {this.generatePendingApproval(current)}
      </FormControl>
    )
    if (current._listBind) {
      return selectionBody
    }
    return <form autoComplete='off'>{selectionBody}</form>
  }

  processValueToPreFillForListBind(current) {
    const raw = current._listBind.value
    if (!current.options) {
      return current.multiSelect ? [] : ''
    }
    if (current.multiSelect) {
      const keys = Array.isArray(raw) ? raw : (raw ? [raw] : [])
      const value = []
      for (const k of keys) {
        for (const o of current.options) {
          if (o.key === k) {
            value.push({
              key: o.key,
              text: o.label || o.text,
            })
          }
        }
      }
      return value
    }
    for (const o of current.options) {
      if (o.key === raw) {
        return { key: o.key, text: o.label || o.text }
      }
    }
    return ''
  }

  processValueToPreFill = (current) => {
    let value
    if (current.multiSelect) {

      if (!this.state[current.key] || (!current.options && !current.addOptions)) return []

      const raw = this.state[current.key]
      const keys = Array.isArray(raw) ? raw : [raw]
      value = []
      for (const k of keys) {
        let found = false
        for (const o of (current.options || [])) {
          if (o.key === k) {
            found = true
            value.push({
              key: o.key,
              text: o.label || o.text
            })
          }
        }
        // addOptions values may not be in the (regenerated) options list; keep them visible
        if (!found && current.addOptions && k) {
          value.push({ key: k, text: String(k) })
        }
      }
    } else {

      if (!this.state[current.key] || !current.options) return ''

      for (const o of current.options) {
        if (o.key === this.state[current.key]) {
          value = {
            key: o.key,
            label: o.label || o.text,
            text: o.label || o.text
          }
        }
      }
      if (value === undefined && this.state[current.key]) {
        const stored = String(this.state[current.key])
        value = { key: stored, label: stored, text: stored }
      }
    }
    return value
  }

  /** Entity add/edit and filter fields: new addOptions values stay on the field only (no catalog API). */
  shouldPromoteAddOptionsLocally(current) {
    if (current.addOptionsLocal) return true
    const formType = this.props.formDefinition && this.props.formDefinition.formType
    return !!(current.addOptions && (formType === 'add' || formType === 'edit'))
  }

  promoteAddOptionInField(current, optionKey, multiSelectValue) {
    if (!current.options) {
      current.options = []
    }
    const displayLabel = String(optionKey)
    if (!current.options.some(o => o && String(o.key) === displayLabel)) {
      current.options.push({ key: displayLabel, label: displayLabel })
    }
    const newOption = { key: displayLabel, label: displayLabel, text: displayLabel }
    const currentSelectedOptions = current.multiSelect
      ? (multiSelectValue || []).filter(obj => obj && obj.key)
      : null
    return current.multiSelect ? [...currentSelectedOptions, newOption] : newOption
  }

  resolveAddOptionsSelection = async(value, current) => {
    let errorMessage = ''
    const newlyAddedOption = current.multiSelect ? value.find(obj => !obj.key) : (value && !value.key) ? value : undefined
    if (newlyAddedOption && current.addOptions) {
      const rawInput = newlyAddedOption.value != null && newlyAddedOption.value !== ''
        ? newlyAddedOption.value
        : (newlyAddedOption.text || '')
      const formattedValue = Helper.formatAddOptionInput(rawInput, current.addOptionsFormatter)
      const optionKey = formattedValue || rawInput
      if (!optionKey) {
        value = current.multiSelect ? (value || []).filter(obj => obj && obj.key) : null
        return { value, errorMessage }
      }
      if (this.props.onFormFieldOptionAdd) {
        current.disabled = true
        this.setState({formFieldUpdateProgress:current.key})
        try {
          const updatedOptionsList = await this.props.onFormFieldOptionAdd({
            formField: current,
            value: optionKey,
            formId: this.props.formDefinition.formId
          })
          const currentSelectedOptions = current.multiSelect ? value.filter(obj => obj.key) : {}
          let newOption = {}
          if (updatedOptionsList && updatedOptionsList.length > 0) {
            updatedOptionsList.forEach((x) => {
              const optionLabel = (x.label || x.text || x.title)
              if (optionLabel === formattedValue
                || optionLabel === newlyAddedOption.value
                || (x.key || x.id) === formattedValue
                || (x.key || x.id) === optionKey) {
                newOption = { key: x.key || x.id, text: optionLabel || optionKey }
              }
            })
          }
          value = current.multiSelect ? (Object.keys(newOption).length > 0 ? [...currentSelectedOptions, newOption] : [...currentSelectedOptions]) : newOption;
          if (this.state.formFieldUpdateProgress !== null) this.setState({formFieldUpdateProgress:null})
          current.disabled = false
        } catch(err) {
          if(err) console.log(err)
          errorMessage = Helper.getErrorMsg(err)
          if (this.state.formFieldUpdateProgress !== null) this.setState({formFieldUpdateProgress:null})
          current.disabled = false
        }
      } else if (this.shouldPromoteAddOptionsLocally(current)) {
        value = this.promoteAddOptionInField(current, optionKey, value)
      } else {
        value = current.multiSelect ? (value || []).filter(obj => obj && obj.key) : null
      }
    }
    return { value, errorMessage }
  }

  onListBindAutoCompleteChange = async(value, current) => {
    const { value: resolved, errorMessage } = await this.resolveAddOptionsSelection(value, current)
    if (errorMessage) {
      if (current.v_required) {
        current.valid = false
        current.v_required.err = errorMessage
      }
      return
    }
    if (current.multiSelect) {
      const valDictByKey = {}
      const keys = []
      for (const v of (resolved || [])) {
        if (v && v.key && !valDictByKey[v.key]) {
          valDictByKey[v.key] = true
          keys.push(v.key)
        }
      }
      current._listBind.onChange(keys)
    } else {
      current._listBind.onChange((resolved && resolved.key) || '')
    }
  }

  onAutoCompleteChange = async(value, current) => {
    const newlyAddedOption = current.multiSelect ? value.find(obj => !obj.key) : (value && !value.key) ? value : undefined
    if (newlyAddedOption && current.addOptions && this.props.onFormFieldOptionAdd && !current.multiSelect && !current._listBind) {
      // NOTE: We're assigning an '  ' value to the text box since there's no key present.
      // This ensures that autocomplete displays nothing until we replace the option after the API call.
      // This issue arises with single-select, as the default behavior selects the value when the key is an empty string ('').
      this.setState({[current.key]:'   '})
    }
    const { value: resolved, errorMessage } = await this.resolveAddOptionsSelection(value, current)
    value = resolved
    const field = this.fields.find(o => o.key === current.key)
    if (field) field.valid = true;
    if (!current.multiSelect) {
      this.setFieldValueAndResetDependents(current.key, (value && value.key) || '')
    } else {
      const valDictByKey = {}
      const vals = []
      for (const v of (value || [])) {
        if (v && v.key && !valDictByKey[v.key]) {
          valDictByKey[v.key] = true
          vals.push(v.key)
        }
      }
      if (errorMessage) {
        current.valid = false
        if (current.v_required) {
          current.v_required.err = errorMessage
        }
      }
      this.setFieldValueAndResetDependents(current.key, vals)
    }
  }

  updateListField(listKey, rowId, patch) {
    const listField = this.fields.find(f => f.key === listKey && f.type === 'list')
    if (listField && listField._listRowErrors && listField._listRowErrors[rowId]) {
      for (const key of Object.keys(patch)) {
        delete listField._listRowErrors[rowId][key]
      }
      if (Object.keys(listField._listRowErrors[rowId]).length === 0) {
        delete listField._listRowErrors[rowId]
      }
      if (!hasListRowErrors(listField._listRowErrors)) {
        listField.valid = true
      }
    }
    const cfg = getListConfig(listField)
    const rowFields = cfg.rowFields || []
    let nextRows = (this.state[listKey] || []).map((row) => {
      if (row.id !== rowId) {
        return row
      }
      const next = { ...row, ...patch }
      const keysWithAnyMatchingCondition = new Set()
      for (const rf of rowFields) {
        if (!rf.condition || this.isRowConditionMatch(next, rf.condition)) {
          keysWithAnyMatchingCondition.add(rf.key)
        }
      }
      for (const rf of rowFields) {
        if (rf.resetsRowKeys && Object.prototype.hasOwnProperty.call(patch, rf.key)) {
          for (const rk of rf.resetsRowKeys) {
            next[rk] = ''
          }
        }
        if (rf.condition && !this.isRowConditionMatch(next, rf.condition) && !keysWithAnyMatchingCondition.has(rf.key)) {
          next[rf.key] = ''
        }
      }
      return next
    })
    const arc = cfg.addRowConstraint
    if (arc && nextRows.length > 1) {
      const anchor = (arc.lockToFirstRowValue && patch[arc.rowFieldKey] && nextRows.find(r => r.id === rowId)) || nextRows[0]
      const firstVal = anchor[arc.rowFieldKey]
      const max = (arc.maxItemsByValue && Object.prototype.hasOwnProperty.call(arc.maxItemsByValue, firstVal))
        ? arc.maxItemsByValue[firstVal]
        : cfg.maxItems
      if (max != null && nextRows.length > max) {
        const kept = nextRows.filter(r => r !== anchor).slice(0, max - 1)
        nextRows = nextRows.filter(r => r === anchor || kept.includes(r))
      }
      if (arc.lockToFirstRowValue && firstVal) {
        nextRows = nextRows.map(r =>
          r[arc.rowFieldKey] === firstVal ? r : { ...r, [arc.rowFieldKey]: firstVal }
        )
      }
    }
    this.setFieldValueAndResetDependents(listKey, nextRows)
  }

  addListRow(listKey) {
    const listField = this.fields.find(f => f.key === listKey && f.type === 'list')
    if (!listField) {
      return
    }
    const rows = this.state[listKey] || []
    const { effectiveMax } = this.resolveAddRowConstraint(listField, rows)
    if (effectiveMax != null && rows.length >= effectiveMax) {
      return
    }
    this.setFieldValueAndResetDependents(listKey, [...rows, newListRow(getListConfig(listField).rowFields)])
  }

  removeListRow(listKey, rowId) {
    const listField = this.fields.find(f => f.key === listKey && f.type === 'list')
    if (!listField) return
    const cfg = getListConfig(listField)
    const rows = this.state[listKey] || []
    if (rows.length <= (cfg.minItems || 0)) return
    const nextRows = rows.filter(r => r.id !== rowId)
    this.setFieldValueAndResetDependents(listKey, nextRows)
  }

  moveListRow(listKey, fromIndex, toIndex) {
    const rows = this.state[listKey] || []
    if (!rows[fromIndex] || !rows[toIndex]) return
    const nextRows = rows.slice()
    nextRows[fromIndex] = rows[toIndex]
    nextRows[toIndex] = rows[fromIndex]
    this.setFieldValueAndResetDependents(listKey, nextRows)
  }

  resolveAddRowConstraint(current, rows) {
    const cfg = getListConfig(current)
    const arc = cfg.addRowConstraint
    let effectiveMax = cfg.maxItems
    let lockedFieldKey = null
    let lockedFieldValue = null
    if (arc && rows.length > 0) {
      const firstVal = rows[0][arc.rowFieldKey]
      if (arc.maxItemsByValue && Object.prototype.hasOwnProperty.call(arc.maxItemsByValue, firstVal)) {
        effectiveMax = arc.maxItemsByValue[firstVal]
      }
      if (arc.lockToFirstRowValue && firstVal) {
        lockedFieldKey = arc.rowFieldKey
        lockedFieldValue = firstVal
      }
    }
    return { effectiveMax, lockedFieldKey, lockedFieldValue }
  }

  generateList(current, formControlParams, errorTag) {
    const cfg = getListConfig(current)
    const rowFields = cfg.rowFields || []
    const rows = this.state[current.key] || []
    const disabled = this.shouldDisable(current)
    const { effectiveMax, lockedFieldKey, lockedFieldValue } = this.resolveAddRowConstraint(current, rows)
    const atMax = effectiveMax != null && rows.length >= effectiveMax
    const minItems = cfg.minItems || 0
    const atMin = rows.length <= minItems
    const additionalListActions = typeof cfg.additionalListActions === 'function'
      ? cfg.additionalListActions({
        getValue: (key) => this.state[key],
        setValue: (key, value) => this.setFieldValueAndResetDependents(key, value),
      })
      : null
    const showHeader = cfg.title || cfg.addButtonText || additionalListActions
    const listRowErrors = current._listRowErrors || {}
    const hasRowErrors = hasListRowErrors(listRowErrors)
    return (
      <div key={current.key}>
        <FormControl className={css(Styles.formControlMain)} fullWidth {...formControlParams}>
          <div className={css(Styles.listSection)}>
            {showHeader && (
              <div className={css(Styles.listSectionHeader)}>
                {cfg.title ? <p className={css(Styles.label, Styles.listSectionTitle)}>{cfg.title}</p> : <span />}
                {(additionalListActions || cfg.addButtonText) && (
                  <div className={css(Styles.listHeaderActions)}>
                    {additionalListActions}
                    {cfg.addButtonText && (
                      <button
                        type='button'
                        className={css(Styles.listAddButton)}
                        onClick={() => this.addListRow(current.key)}
                        disabled={disabled || atMax}
                        aria-label={cfg.addButtonText}
                      >
                        <i className={`material-icons-outlined ${css(Styles.listAddButtonIcon)}`}>add</i>
                        <span className={css(Styles.listAddButtonLabel)}>{cfg.addButtonText}</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
            {rows.map((row, rowIdx) => (
              <div key={row.id} className={css(Styles.listRow)}>
                {cfg.reorderable && (
                  <div className={disabled ? css(Styles.listRowOrder, Styles.listRowOrderDisabled) : css(Styles.listRowOrder)}>
                    {UiHelper.buttonOrderChange(row, {
                      index: rowIdx,
                      items: rows,
                      orderUp: () => this.moveListRow(current.key, rowIdx, rowIdx - 1),
                      orderDown: () => this.moveListRow(current.key, rowIdx, rowIdx + 1),
                    })}
                  </div>
                )}
                <div className={css(Styles.listRowGrid)}>
                  {rowFields.map((rf) => {
                    if (rf.condition && !this.isRowConditionMatch(row, rf.condition)) {
                      return null
                    }
                    let resolved = rf
                    if (Array.isArray(rf.options)) {
                      let options = rf.options.slice()
                      if (lockedFieldKey && rf.key === lockedFieldKey && rowIdx > 0) {
                        options = options.filter(o => o.key === lockedFieldValue)
                      }
                      resolved = { ...rf, options }
                    }
                    this.ensureListRowSelectOptions(resolved, row[rf.key])
                    const uiKey = resolved._uiKey || resolved.key
                    const cellErr = listRowErrors[row.id] && listRowErrors[row.id][rf.key]
                    const subParams = {}
                    const bound = {
                      ...resolved,
                      key: `${current.key}_${row.id}_${uiKey}`,
                      valid: !cellErr,
                      disabled: disabled || resolved.disabled,
                      _listBind: {
                        value: row[rf.key],
                        onChange: (val) => this.updateListField(current.key, row.id, { [rf.key]: val }),
                      },
                    }
                    this.setFormControlParams(bound, subParams)
                    const rowErrorTag = cellErr
                      ? <FormHelperText className={css(Styles.errorMsg)}>{cellErr}</FormHelperText>
                      : null
                    let fieldEl = null
                    if (resolved.type === 'edit_text') {
                      fieldEl = this.generateEditText(bound, subParams, rowErrorTag)
                    } else if (resolved.type === 'radio') {
                      bound.multiSelect = false
                      fieldEl = this.generateAutoCompleteSelection(bound, subParams, rowErrorTag)
                    } else if (resolved.type === 'check_box') {
                      bound.multiSelect = true
                      fieldEl = this.generateAutoCompleteSelection(bound, subParams, rowErrorTag)
                    }
                    if (!fieldEl) {
                      return null
                    }
                    return (
                      <div key={`${row.id}-${uiKey}`} className={css(Styles.listRowField)}>
                        {fieldEl}
                      </div>
                    )
                  })}
                </div>
                <button
                  type='button'
                  className={css(Styles.listRowRemove)}
                  onClick={() => this.removeListRow(current.key, row.id)}
                  disabled={disabled || atMin}
                  aria-label='Remove row'
                  title='Remove'
                >
                  <i className='material-icons-outlined'>close</i>
                </button>
              </div>
            ))}
          </div>
        </FormControl>
        {!hasRowErrors && errorTag}
      </div>
    )
  }

  generatePendingApproval(current) {
    return current.pendingApproval ? <p className={css(Styles.pendingApprovalLabel)}>{Helper.getString('pendingAppr')}</p> : ''
  }

  shouldDisable(current) {
    // if (Helper.inArray(['add'], this.props.formDefinition.formType)) return false
    if (current && current.disabled && this.props.formDefinition.formType !== 'add') {
      return true
    }
    if (this.props.formDefinition.disableAlwaysFormFields && this.props.formDefinition.disableAlwaysFormFields.indexOf(current.key) > -1) {
      return true
    }
    if (Helper.inArray(['delete', 'del'], this.props.formDefinition.formType)) {
      return true
    }
    return Helper.inArray(['delete', 'del', 'approve'], this.props.formDefinition.formType) ||
      this.state.inProcess
  }
}

const Styles = AppConfig.CUSTOM_STYLE ? CustomDynamicFormStyles : StyleSheet.create({
  form: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    // padding: AppTheme.pagePadding,
    '@media (max-width: 320px)': {
      maxWidth: 300,
    }
  },
  formWithPreview: {
    '@media (max-width: 1024px)': {
      paddingBottom: 0,
    },
  },
  previewLayout: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 16,
    width: '100%',
    minWidth: 0,
    '@media (max-width: 1024px)': {
      flexDirection: 'column',
      gap: 12,
      paddingBottom: 110,
    },
  },
  previewLayoutFormPane: {
    flex: '1 1 auto',
    minWidth: 0,
    width: '100%',
  },
  leftGridPadding: {
    paddingRight: 10,
    '@media (max-width: 767px)': {
      paddingRight: 0,
    }
  },
  rightGridPadding: {
    paddingLeft: 10,
    '@media (max-width: 767px)': {
      paddingLeft: 0,
    }
  },
  errorMsg: {
    paddingRight: 20,
    color: '#f44336'
  },
  successMsg: {
    paddingRight: 20,
    color: 'green'
  },
  marginLeft: {
    color: 'white'
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap'
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    marginTop: 20,
    marginBottom: 30
  },
  pendingApprovalLabel: {
    marginTop: 0,
    marginBottom: 0,
    color: 'orange',
    fontSize: 10
  },
  openDoc: {
    marginTop: 0,
    marginBottom: 0,
    color: 'black',
    fontSize: 10,
    cursor: 'pointer',
  },
  uploadFieldLabel: {
    marginTop: 10,
  },
  buttonDelete: {
    backgroundColor: '#FF0D0D'
  },
  label: {
    fontSize: 14,
    color: 'grey',
    margin: 0,
    // marginBottom: 8,
    display: 'flex',
  },
  formControl: {
    borderRadius: AppTheme.borderRadius,
    border: '1px solid #E5E5E5',
    overflow: 'hidden'
  },
  textInput: {
    padding: '4px 0px'
  },
  htmlFieldContent: {
    width: '100%',
    boxSizing: 'border-box',
    padding: 16,
    border: '1px solid #E5E5E5',
    borderRadius: AppTheme.borderRadius,
    color: '#222222',
    backgroundColor: '#FFFFFF',
    maxHeight: '70vh',
    overflow: 'auto',
    wordBreak: 'break-word',
  },
  textInputStyles: {
    padding: '11px 20px',
    flex: 1,
    textAlign: 'left',
    color: '#222222',
    fontSize: 16
  },
  textInputStylesDisabled: {
    backgroundColor: '#f5f5f5',
  },
  pickerStyle: {
    marginTop: 0
  },
  pickerInputStyles: {
    padding: '8px 20px',
    flex: 1,
    textAlign: 'left',
    color: '#222222',
    fontSize: 16,
    borderRadius: AppTheme.borderRadius,
    border: '1px solid #E5E5E5',
    overflow: 'hidden'
  },
  formInputStyle: {
    '::-webkit-scrollbar': {
      display: 'none'
    }
  },
  tag: {
    backgroundColor: AppTheme.secondaryColor,
    color: '#222222',
    fontSize: 13,
    borderRadius: 8,
    height: 26
  },
  popupIndicator: {
    marginRight: 5,
  },
  dialog: {
    zIndex: 130000,
    fontSize: 16,
    color: '#222222',
  },
  paper: {
    borderRadius: AppTheme.borderRadius,
  },

  modalCloseIcon: {
    width: 12,
    height: 12,
    padding: 1,
    borderRadius: 6
  },

  fieldOuterContainer: {
    display: 'flex'
  },
  fieldLeftInnerContainer: {
    flex: 1,
    paddingRight: 10,
    '@media (max-width: 767px)': {
      paddingRight: 0,
    }
  },
  fieldRightInnerContainer: {
    flex: 1,
    paddingLeft: 10,
    '@media (max-width: 767px)': {
      paddingLeft: 0,
    }
  },
  delFieldContainer: {
    width: '25%',
    marginRight: 20
  },
  formControlMain: {
    marginTop: 0,
    marginBottom: 0
  },
  optionImageContainer: {
    width: 20,
    height: 20,
  },
  optionImage: {
    width: 'auto',
    height: 'auto',
    maxWidth: '100%',
    maxHeight: '100%'
  },
  optionImageContainerSpec: {
    marginRight: 9,
    marginLeft: 5
  },
  // requiredIndicator: {
  //   display: 'flex',
  //   color: 'grey',
  //   lineHeight: 0,
  //   alignItems: 'center',
  //   fontSize: 13,
  //   justifyContent: 'flex-end',
  //   marginLeft: 4
  // },
  requiredIndicator: {
    opacity: 0.5,
    height: 7,
    width: 7
  },
  labelContainer: {
    display: 'flex',
    marginBottom: 8
  },
  tooltip: {
    zIndex: 9999999999
  },
  autoCompleteInputRoot: {
    paddingRight: 56
  },
  optionLoaderSpinner:{
    marginRight: 15
  },
  addOptionStyle: {
    color: AppTheme.buttonBg
  },
  healthOptionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  healthDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
    display: 'inline-block',
  },
  healthDotClickable: {
    cursor: 'pointer',
  },
  listSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  listSectionHeader: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  listHeaderActions: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
    flexShrink: 0,
    gap: 12,
  },
  listSectionTitle: {
    marginBottom: 0,
  },
  listAddButton: {
    border: `1px solid ${AppTheme.primaryColor || '#2563EB'}`,
    borderRadius: AppTheme.borderRadius,
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    padding: '10px 18px',
    minHeight: 40,
    boxSizing: 'border-box',
    display: 'inline-flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    color: AppTheme.primaryColor || '#2563EB',
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1,
    outline: 'none',
    ':disabled': {
      opacity: 0.45,
      cursor: 'not-allowed',
    },
  },
  listAddButtonIcon: {
    fontSize: 20,
    lineHeight: 1,
    display: 'block',
  },
  listAddButtonLabel: {
    lineHeight: 1,
    letterSpacing: 0.2,
  },
  listRow: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'flex-start',
    gap: 10,
    width: '100%',
    minWidth: 0,
  },
  listRowGrid: {
    flex: '1 1 auto',
    width: '100%',
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'flex-start',
    gap: 8,
    minWidth: 0,
    overflowX: 'visible',
    paddingBottom: 4,
    paddingRight: 2,
  },
  listRowField: {
    flex: '1 1 0',
    width: 0,
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box',
    '& > *': {
      width: '100%',
      minWidth: 0,
      maxWidth: '100%',
    },
  },
  listRowChip: {
    height: 'auto',
    minHeight: 26,
    whiteSpace: 'normal',
  },
  listRowChipLabel: {
    paddingTop: 3,
    paddingBottom: 3,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  },
  listRowChipDeleteIcon: {
    flexShrink: 0,
  },
  listRowOrder: {
    flexShrink: 0,
    alignSelf: 'flex-start',
    marginTop: 27,
  },
  listRowOrderDisabled: {
    opacity: 0.45,
    pointerEvents: 'none',
  },
  listRowRemove: {
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    padding: 8,
    flexShrink: 0,
    alignSelf: 'flex-start',
    marginTop: 30,
    marginLeft: 4,
    color: '#6B7280',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    ':hover': {
      color: '#111827',
    },
    ':disabled': {
      opacity: 0.45,
      cursor: 'not-allowed',
    },
  },
})
